import React, { useState, useRef } from "react";
import { Popover, Chip, Stack } from "@mui/material";
import { colorForBgColor } from "@jumbo/utils";
import Div from "@jumbo/shared/Div";

const withPopoverChips = (WrappedComponent) => {
  const WithPopoverChips = (props) => {
    const { chips, max, defaultColor, chipProps, spacing, ...restProps } =
      props;
    const [anchorEl, setAnchorEl] = useState(null);
    const chipRef = useRef(null);

    const chipsToShow = max !== -1 ? chips.slice(0, max) : chips;
    const remainingChips = max !== -1 ? chips.slice(max) : [];

    const handlePopoverOpen = (event) => {
      setAnchorEl(event.currentTarget);
    };

    const handlePopoverClose = () => {
      setAnchorEl(null);
    };

    const open = Boolean(anchorEl);

    return (
      <Div sx={{ display: "flex", alignItems: "center", flexDirection: "row" }}>
        <WrappedComponent
          {...props}
          chips={chipsToShow}
          spacing={spacing}
          chipProps={(chip) => {
            const customProps = chipProps ? chipProps(chip) : {};
            return {
              ...customProps,
              ...(remainingChips.length > 0 &&
              chipsToShow.length - 1 === chips.indexOf(chip)
                ? { ref: chipRef } // Only attach the ref, no onMouseEnter
                : {}),
            };
          }}
        />
        {remainingChips.length > 0 && (
          <>
            <Chip
              label={`+${remainingChips.length}`}
              size={restProps?.size || "small"}
              sx={{
                bgcolor: "defaultColor",
                color: colorForBgColor(defaultColor),
                marginLeft: "4px",
              }}
              onMouseEnter={handlePopoverOpen}
              onMouseLeave={handlePopoverClose}
            />
            <Popover
              open={open}
              anchorEl={anchorEl}
              onClose={handlePopoverClose}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "left",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "left",
              }}
              sx={{
                pointerEvents: "none",
              }}
            >
              <Stack
                direction="row"
                spacing={spacing || 1}
                sx={{
                  p: 1,
                  bgcolor: "background.paper",
                  borderRadius: 1,
                  boxShadow: 1,
                }}
              >
                {remainingChips.map((chip, index) => {
                  const label = chip?.label || chip.name;
                  const customProps = chipProps ? chipProps(chip) : {};
                  return (
                    <Chip
                      key={index}
                      label={label}
                      sx={{
                        bgcolor: "primary.main",
                        color: colorForBgColor("primary"),
                        "&:hover": {
                          bgcolor: "primary.main",
                          color: colorForBgColor("primary"),
                        },
                        ...customProps.sx,
                      }}
                      size={restProps?.size || "small"}
                    />
                  );
                })}
              </Stack>
            </Popover>
          </>
        )}
      </Div>
    );
  };

  WithPopoverChips.propTypes = WrappedComponent.propTypes;
  WithPopoverChips.defaultProps = WrappedComponent.defaultProps;

  return WithPopoverChips;
};

export default withPopoverChips;
